// EM2c gameplay on the CGPT Mars world.
// Gameplay/placement coordinates remain axial hex coordinates; this scene only
// translates those stable coordinates onto the visible terrain surface.

import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Html, OrbitControls, Sparkles } from "@react-three/drei";
import * as THREE from "three";
import { useGame } from "../em2c-store";
import { HEX_SIZE, axialToWorld, worldToAxial, rotateFootprint, getNeighbors } from "../hexgrid";
import { HexBuildingMesh } from "./HexBuildings";
import { HexGridOverlay, HexGridLines, HexPlacementGhost, createHexGeometry } from "./HexGridViz";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";
import { MOVE_BUILDING_COST } from "../systems/MoveBuildingIntegration";
import { CGPTAtmosphere, CGPTRocks, CGPTTerrain, marsHeight } from "./CGPTWorldTerrain";

function terrainY(x, z, offset = 0) {
  return marsHeight(x, z) + offset;
}

function buildingBaseY(b) {
  const def = EM2C_BUILDINGS[b.type];
  const footprint = rotateFootprint(def?.footprintHexes || [{ q: 0, r: 0 }], b.rotation || 0);
  let highest = -Infinity;

  for (const cell of footprint) {
    const pos = axialToWorld(b.q + cell.q, b.r + cell.r);
    highest = Math.max(highest, marsHeight(pos.x, pos.z));
  }

  if (Number.isFinite(highest)) return highest + 0.02;
  const center = axialToWorld(b.q, b.r);
  return terrainY(center.x, center.z, 0.02);
}

function DroneUnit({ d }) {
  const ref = useRef();

  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.position.x += (d.x - ref.current.position.x) * 0.05;
    ref.current.position.z += (d.z - ref.current.position.z) * 0.05;
    const ground = marsHeight(ref.current.position.x, ref.current.position.z);
    ref.current.position.y = ground + 2.5 + Math.sin(clock.elapsedTime * 2 + (d.id.charCodeAt(0) || 0)) * 0.2;

    const r1 = ref.current.children[1];
    const r2 = ref.current.children[2];
    if (r1) r1.rotation.y = clock.elapsedTime * 15;
    if (r2) r2.rotation.y = clock.elapsedTime * 15;
  });

  return (
    <group ref={ref} position={[d.x, terrainY(d.x, d.z, 2.5), d.z]}>
      <mesh castShadow>
        <boxGeometry args={[0.6, 0.3, 0.6]} />
        <meshStandardMaterial color="#3a3f4b" metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.6, 0.1, 0.05, 4]} />
        <meshStandardMaterial color="#1a1f28" side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 0.15, 0]} rotation={[Math.PI, 0, 0]}>
        <cylinderGeometry args={[0.6, 0.1, 0.05, 4]} />
        <meshStandardMaterial color="#1a1f28" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function Drones() {
  const drones = useGame((s) => s.drones);
  return <group>{drones.map((d) => <DroneUnit key={d.id} d={d} />)}</group>;
}

function Colonists() {
  const colonists = useGame((s) => s.colonists);
  const refs = useRef({});

  useFrame((_, delta) => {
    const lerpFactor = Math.min(1, delta * 5);
    colonists.forEach((c) => {
      const ref = refs.current[c.id];
      if (!ref) return;
      ref.position.x += (c.x - ref.position.x) * lerpFactor;
      ref.position.z += (c.z - ref.position.z) * lerpFactor;
      const y = marsHeight(ref.position.x, ref.position.z);
      ref.position.y += (y - ref.position.y) * lerpFactor;
    });
  });

  return (
    <group>
      {colonists.map((c) => (
        <group
          key={c.id}
          ref={(el) => { if (el) refs.current[c.id] = el; }}
          position={[c.x, terrainY(c.x, c.z), c.z]}
        >
          <mesh castShadow position={[0, 0.24, 0]}>
            <capsuleGeometry args={[0.12, 0.22, 4, 8]} />
            <meshStandardMaterial
              color={c.health > 0.5 ? "#e6e6ea" : "#ff6666"}
              emissive={c.health > 0.5 ? "#00e5ff" : "#ff3366"}
              emissiveIntensity={0.15}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function HexPlacementPreview() {
  const placementType = useGame((s) => s.placementType);
  const hoverHex = useGame((s) => s.hoverHex);
  const placementRotation = useGame((s) => s.placementRotation);
  const isValidHexPlacement = useGame((s) => s.isValidHexPlacement);

  if (!placementType || !hoverHex) return null;
  const building = EM2C_BUILDINGS[placementType];
  if (!building) return null;

  const rotatedFootprint = rotateFootprint(
    building.footprintHexes || [{ q: 0, r: 0 }],
    placementRotation
  );
  const result = isValidHexPlacement(placementType, hoverHex.q, hoverHex.r, placementRotation);

  return (
    <HexPlacementGhost
      originQ={hoverHex.q}
      originR={hoverHex.r}
      footprint={rotatedFootprint}
      valid={result.valid}
      placement={result.placement || "valid"}
    />
  );
}

function MovePlacementPreview() {
  const moveBuildingId = useGame((s) => s.moveBuildingId);
  const moveCandidate = useGame((s) => s.moveCandidate);
  const hoverHex = useGame((s) => s.hoverHex);
  const buildings = useGame((s) => s.buildings);
  const isValidMovePlacement = useGame((s) => s.isValidMovePlacement);
  const confirmMoveBuilding = useGame((s) => s.confirmMoveBuilding);
  const cancelMoveBuilding = useGame((s) => s.cancelMoveBuilding);

  if (!moveBuildingId) return null;
  const instance = buildings.find((item) => item.id === moveBuildingId);
  const def = instance ? EM2C_BUILDINGS[instance.type] : null;
  const target = moveCandidate || hoverHex;
  if (!instance || !def || !target || typeof isValidMovePlacement !== "function") return null;

  const footprint = rotateFootprint(def.footprintHexes || [{ q: 0, r: 0 }], instance.rotation || 0);
  const result = isValidMovePlacement(target.q, target.r);
  const world = axialToWorld(target.q, target.r);
  const locked = Boolean(moveCandidate);

  return (
    <>
      <HexPlacementGhost
        originQ={target.q}
        originR={target.r}
        footprint={footprint}
        valid={result.valid}
        placement={result.placement || (result.valid ? "valid" : "blocked")}
      />

      {locked && result.valid && (
        <Html
          position={[world.x, terrainY(world.x, world.z, 2.2), world.z]}
          center
          zIndexRange={[180, 140]}
          style={{ pointerEvents: "auto" }}
        >
          <div
            className="w-64 border border-cyan-400/35 bg-[#090C10]/97 backdrop-blur-xl shadow-[0_12px_35px_rgba(0,0,0,.65)] text-white"
            style={{ fontFamily: "Rajdhani, sans-serif" }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-2 border-b border-white/10">
              <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-cyan-400">Move Building</div>
              <div className="text-sm font-semibold uppercase tracking-wide mt-0.5">Place {def.name} here?</div>
            </div>
            <div className="px-3 py-2 flex items-center justify-between text-[10px] font-mono text-slate-400">
              <span>Immediate move</span>
              <span className="text-[#e8a37e]">{MOVE_BUILDING_COST.machine_parts} Machine Parts</span>
            </div>
            <div className="grid grid-cols-2 border-t border-white/10">
              <button
                data-testid="confirm-move-building"
                onClick={(e) => { e.stopPropagation(); confirmMoveBuilding?.(); }}
                className="py-2 text-[10px] font-mono uppercase tracking-widest text-[#00fa9a] hover:bg-[#00fa9a]/10 border-r border-white/10"
              >
                Confirm
              </button>
              <button
                data-testid="cancel-move-building"
                onClick={(e) => { e.stopPropagation(); cancelMoveBuilding?.(); }}
                className="py-2 text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-white hover:bg-white/5"
              >
                Cancel
              </button>
            </div>
          </div>
        </Html>
      )}
    </>
  );
}

function InteractiveTerrain() {
  const setHoverHex = useGame((s) => s.setHoverHex);
  const placeBuilding = useGame((s) => s.placeBuilding);
  const placementType = useGame((s) => s.placementType);
  const moveBuildingId = useGame((s) => s.moveBuildingId);
  const chooseMoveDestination = useGame((s) => s.chooseMoveDestination);
  const selectBuilding = useGame((s) => s.selectBuilding);

  return (
    <CGPTTerrain
      onPointerMove={(e) => {
        if (!placementType && !moveBuildingId) return;
        e.stopPropagation();
        setHoverHex(worldToAxial(e.point.x, e.point.z));
      }}
      onPointerOut={() => {
        if (placementType || moveBuildingId) setHoverHex(null);
      }}
      onClick={(e) => {
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent("mars:world-click"));
        const hex = worldToAxial(e.point.x, e.point.z);
        if (moveBuildingId) {
          chooseMoveDestination?.(hex.q, hex.r);
        } else if (placementType) {
          placeBuilding(hex.q, hex.r);
        } else {
          selectBuilding(null);
          window.dispatchEvent(new CustomEvent("mars:hex-selected", { detail: hex }));
        }
      }}
    />
  );
}

function StaticLighting() {
  const stormActive = useGame((s) => s.storm.active);
  return (
    <>
      <hemisphereLight args={[stormActive ? "#d58e69" : "#ffc9a8", "#35180f", stormActive ? 1.1 : 1.7]} />
      <directionalLight
        position={[34, 38, 22]}
        intensity={stormActive ? 1.5 : 3.0}
        color={stormActive ? "#c88060" : "#ffd1a6"}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
    </>
  );
}

function Storm() {
  const active = useGame((s) => s.storm.active);
  if (!active) return null;
  return <Sparkles count={800} scale={[170, 12, 170]} size={5} color="#c88060" speed={3} />;
}

function CameraControls() {
  const placementType = useGame((s) => s.placementType);
  const moveBuildingId = useGame((s) => s.moveBuildingId);
  const controlsRef = useRef(null);
  const focusAnim = useRef(null);
  const { camera } = useThree();

  useEffect(() => {
    const focus = (event) => {
      const controls = controlsRef.current;
      if (!controls) return;
      const detail = event?.detail || {};
      let x = Number(detail.x);
      let z = Number(detail.z);
      if ((!Number.isFinite(x) || !Number.isFinite(z)) && Number.isFinite(detail.q) && Number.isFinite(detail.r)) {
        const world = axialToWorld(detail.q, detail.r);
        x = world.x;
        z = world.z;
      }
      if (!Number.isFinite(x) || !Number.isFinite(z)) return;

      const y = marsHeight(x, z) + 0.8;
      const fromTarget = controls.target.clone();
      const fromPosition = camera.position.clone();
      const offset = fromPosition.clone().sub(fromTarget);
      const distance = Math.max(13, Math.min(25, offset.length() || 18));
      const direction = offset.lengthSq() > 0.0001
        ? offset.normalize()
        : new THREE.Vector3(0.65, 0.7, 0.8).normalize();
      const toTarget = new THREE.Vector3(x, y, z);
      const toPosition = toTarget.clone().add(direction.multiplyScalar(distance));

      focusAnim.current = {
        elapsed: 0,
        duration: 0.55,
        fromTarget,
        toTarget,
        fromPosition,
        toPosition,
      };
    };

    window.addEventListener("mars:focus-building", focus);
    return () => window.removeEventListener("mars:focus-building", focus);
  }, [camera]);

  useFrame((_, delta) => {
    const anim = focusAnim.current;
    const controls = controlsRef.current;
    if (!anim || !controls) return;
    anim.elapsed += delta;
    const p = Math.min(1, anim.elapsed / anim.duration);
    const t = 1 - Math.pow(1 - p, 3);
    controls.target.lerpVectors(anim.fromTarget, anim.toTarget, t);
    camera.position.lerpVectors(anim.fromPosition, anim.toPosition, t);
    controls.update();
    if (p >= 1) focusAnim.current = null;
  });

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      target={[0, 0.2, 0]}
      maxPolarAngle={1.25}
      minDistance={9}
      maxDistance={90}
      enablePan={!placementType && !moveBuildingId}
      panSpeed={1.0}
      rotateSpeed={0.72}
      enableDamping
      dampingFactor={0.08}
    />
  );
}

function getHexCornerXZ(cx, cz, i, radius) {
  const angle = (Math.PI / 180) * (60 * i);
  return { x: cx + radius * Math.cos(angle), z: cz + radius * Math.sin(angle) };
}

function DepositZone({ deposit }) {
  const color = deposit.kind === "hydrated_minerals"
    ? "#69d9e8"
    : deposit.kind === "water"
      ? "#69d9e8"
      : "#ffc107";
  const depleted = deposit.remaining <= 0;
  const pct = deposit.capacity > 0 ? clampPct(deposit.remaining / deposit.capacity) : 0;
  const fillOpacity = depleted ? 0.05 : 0.14 + pct * 0.16;
  const lineOpacity = depleted ? 0.15 : 0.85;

  const hexes = deposit.hexes && deposit.hexes.length
    ? deposit.hexes
    : [{ q: deposit.q, r: deposit.r, x: deposit.x, z: deposit.z }];

  const hexFillGeom = useMemo(() => createHexGeometry(HEX_SIZE * 0.94), []);

  const outlineGeom = useMemo(() => {
    const keySet = new Set(hexes.map((h) => `${h.q},${h.r}`));
    const positions = [];
    const radius = HEX_SIZE * 0.94;

    for (const h of hexes) {
      const neighbors = getNeighbors(h.q, h.r);
      for (let i = 0; i < 6; i++) {
        const n = neighbors[i];
        if (keySet.has(`${n.q},${n.r}`)) continue;
        const c1 = getHexCornerXZ(h.x, h.z, i, radius);
        const c2 = getHexCornerXZ(h.x, h.z, i + 1, radius);
        positions.push(
          c1.x, terrainY(c1.x, c1.z, 0.075), c1.z,
          c2.x, terrainY(c2.x, c2.z, 0.075), c2.z
        );
      }
    }

    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, [hexes]);

  return (
    <group>
      {hexes.map((h, i) => (
        <mesh key={i} geometry={hexFillGeom} position={[h.x, terrainY(h.x, h.z, 0.035), h.z]}>
          <meshBasicMaterial color={color} transparent opacity={fillOpacity} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
      ))}

      <lineSegments geometry={outlineGeom}>
        <lineBasicMaterial color={color} transparent opacity={lineOpacity} linewidth={2} />
      </lineSegments>

      <group position={[deposit.x, terrainY(deposit.x, deposit.z), deposit.z]}>
        <mesh position={[0, 0.6, 0]}>
          <cylinderGeometry args={[0.15, 0.4, 1.2, 6]} />
          <meshStandardMaterial color={color} emissive={color} emissiveIntensity={depleted ? 0.1 : 0.8} transparent opacity={depleted ? 0.3 : 1} />
        </mesh>
        <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.5, 0.8, 6]} />
          <meshBasicMaterial color={color} transparent opacity={lineOpacity * 0.6} side={THREE.DoubleSide} />
        </mesh>
      </group>
    </group>
  );
}

function clampPct(v) {
  return Math.max(0, Math.min(1, v));
}

function Deposits() {
  const deposits = useGame((s) => s.deposits);
  if (!deposits) return null;
  return (
    <group>
      {deposits.filter((d) => d.discovered).map((d) => (
        <DepositZone key={d.id} deposit={d} />
      ))}
    </group>
  );
}

export default function Em2cScene() {
  const grid = useGame((s) => s.grid);
  const gridVisible = useGame((s) => s.gridVisible);
  const buildings = useGame((s) => s.buildings);
  const selectedId = useGame((s) => s.selectedId);
  const overlay = useGame((s) => s.overlay);
  const showConnectors = useGame((s) => s.showConnectors);
  const stormActive = useGame((s) => s.storm.active);
  const selectBuilding = useGame((s) => s.selectBuilding);
  const placementType = useGame((s) => s.placementType);
  const moveBuildingId = useGame((s) => s.moveBuildingId);
  const cancelMoveBuilding = useGame((s) => s.cancelMoveBuilding);

  return (
    <Canvas shadows dpr={[1, 2]} camera={{ position: [15, 16, 19], fov: 46, near: 0.1, far: 300 }}>
      <CGPTAtmosphere stormActive={stormActive} />
      <StaticLighting />

      <InteractiveTerrain />
      <CGPTRocks />
      <Deposits />

      {gridVisible && <HexGridOverlay grid={grid} visible={gridVisible} />}
      {gridVisible && <HexGridLines grid={grid} visible={gridVisible} />}

      {buildings.map((b) => {
        const center = axialToWorld(b.q, b.r);
        return (
          <group
            key={b.id}
            position={[0, buildingBaseY(b), 0]}
            onClick={(e) => {
              if (placementType || moveBuildingId) return;
              e.stopPropagation();
              selectBuilding(b.id);
              window.dispatchEvent(new CustomEvent("mars:hex-selected", { detail: null }));
            }}
            onContextMenu={(e) => {
              e.stopPropagation();
              e.nativeEvent?.preventDefault?.();
              if (placementType) return;
              if (moveBuildingId) {
                cancelMoveBuilding?.();
                return;
              }
              const native = e.nativeEvent || e;
              window.dispatchEvent(new CustomEvent("mars:building-context", {
                detail: {
                  buildingId: b.id,
                  clientX: Number(native.clientX || 0),
                  clientY: Number(native.clientY || 0),
                },
              }));
            }}
          >
            <HexBuildingMesh b={b} selected={b.id === selectedId} overlay={overlay} showConnectors={showConnectors} />
            <object3D position={[center.x, 0, center.z]} />
          </group>
        );
      })}

      {placementType && <HexPlacementPreview />}
      {moveBuildingId && <MovePlacementPreview />}

      <Drones />
      <Colonists />

      <Sparkles count={120} scale={[170, 6, 170]} size={2} color="#d09070" speed={0.4} opacity={0.45} />
      <Storm />
      <CameraControls />
    </Canvas>
  );
}